/**
 * Typed testid registry for the `contexts/` bucket — the SINGLE source of truth
 * for every `data-testid` the context-shell components render (TV1-2055 phase 3,
 * rolling out the TV1-1866 pattern to the web-app tail: `components/`, `pages/`,
 * `contexts/`).
 *
 * These are the account-state overlays / banners rendered by the auth context
 * (`contexts/useAuth/*`): the impersonation banner, the provisioning overlay,
 * and the suspension banner / block. Components spread these values into
 * `data-testid`; the LIVE Playwright specs import the SAME object for their
 * selectors. A renamed key changes the string in ONE place and both sides move
 * together — instead of a runtime "element not found" when a testid drifts from
 * the string a spec hardcoded (e2e is not run in CI, so that drift fails
 * silently). The `teselly/no-raw-testid` ESLint rule forbids raw `data-testid`
 * literals under `src/apps/web-app/**` so new testids must land in a registry.
 *
 * Naming convention: `<area>-[section]-[element]` in kebab-case (see
 * `.claude/rules/e2e-testing.md`). Values are preserved verbatim from the
 * pre-existing literals (no rename) so the migration is value-safe.
 */
export const contextsTestIds = {
  // --- useAuth / ImpersonationBanner ---
  /** Impersonation banner container. */
  impersonationBanner: 'impersonation-banner',
  /** Impersonated-user info text. */
  impersonationBannerUserInfo: 'impersonation-banner-user-info',
  /** Stop-impersonation button. */
  impersonationBannerStopButton: 'impersonation-banner-stop-button',

  // --- useAuth / ProvisioningOverlay ---
  /** Overlay container (rendered in both the loading and error states). */
  provisioningOverlay: 'auth-provisioning-overlay',
  /** Error body. */
  provisioningError: 'auth-provisioning-error',
  /** Overlay title (rendered in both states). */
  provisioningTitle: 'auth-provisioning-title',
  /** Support link. */
  provisioningSupportLink: 'auth-provisioning-support-link',
  /** Retry button. */
  provisioningRetryButton: 'auth-provisioning-retry-button',
  /** Logout button. */
  provisioningLogoutButton: 'auth-provisioning-logout-button',
  /** Loading spinner. */
  provisioningLoader: 'auth-provisioning-loader',

  // --- useAuth / SuspensionBanner ---
  /** Suspension banner container. */
  suspensionBanner: 'suspension-banner',
  /** Suspension banner message. */
  suspensionBannerMessage: 'suspension-banner-message',
  /** Contact-support link. */
  suspensionBannerContactLink: 'suspension-banner-contact-link',

  // --- useAuth / SuspensionBlock ---
  /** Full-screen suspension block. */
  suspensionBlock: 'suspension-block',
  /** Block title. */
  suspensionBlockTitle: 'suspension-block-title',
  /** Block body. */
  suspensionBlockBody: 'suspension-block-body',
  /** Contact-support button. */
  suspensionBlockContactButton: 'suspension-block-contact-button'
} as const;
